import React, { useState, useEffect } from 'react';
import { updateFornecedor, consultarCNPJ } from '../services/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, InputAdornment, Snackbar, Alert } from '@mui/material';
import { Fornecedor } from '../services/types';
import { formatCpfCnpj, validateCpfCnpj } from '../utils/validationUtils';

interface FornecedorEditModalProps {
    fornecedor: Fornecedor | null;
    fornecedoresList?: Fornecedor[];
    open: boolean;
    onClose: () => void;
    onFornecedorUpdated: () => void;
}

const FornecedorEditModal: React.FC<FornecedorEditModalProps> = ({ fornecedor, fornecedoresList = [], open, onClose, onFornecedorUpdated }) => {
    const [nome, setNome] = useState('');
    const [cnpj, setCnpj] = useState('');
    const [telefone, setTelefone] = useState('');
    const [email, setEmail] = useState('');
    const [endereco, setEndereco] = useState('');
    const [tipo, setTipo] = useState<'MATERIAL' | 'SERVICO' | 'AMBOS'>('SERVICO');

    const [cpfCnpjError, setCpfCnpjError] = useState('');
    const [isConsulting, setIsConsulting] = useState(false);

    // Snackbar state
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

    useEffect(() => {
        if (fornecedor) {
            setNome(fornecedor.nome_razao_social);
            setCnpj(formatCpfCnpj(fornecedor.cnpj_cpf));
            setTelefone(fornecedor.telefone || '');
            setEmail(fornecedor.email || '');
            setEndereco(fornecedor.endereco || '');
            setTipo(fornecedor.tipo || 'SERVICO');
            setCpfCnpjError('');
        }
    }, [fornecedor]);

    const handleCnpjCpfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        const formattedValue = formatCpfCnpj(rawValue);
        setCnpj(formattedValue);

        if (formattedValue.length > 0) {
            // Verifica duplicidade local ignorando o fornecedor atual
            const isDuplicate = fornecedoresList.find(f => f.cnpj_cpf === formattedValue && f.id !== fornecedor?.id);
            if (isDuplicate) {
                setCpfCnpjError(`Já cadastrado: ${isDuplicate.nome_razao_social}`);
                return;
            }

            const isValid = validateCpfCnpj(formattedValue);
            if (!isValid) {
                setCpfCnpjError('CPF ou CNPJ inválido');
            } else {
                setCpfCnpjError('');
                const cleanValue = formattedValue.replace(/\D/g, '');
                if (cleanValue.length === 14) {
                    setIsConsulting(true);
                    try {
                        const data = await consultarCNPJ(cleanValue);
                        if (data.razao_social) setNome(data.razao_social);
                        
                        let enderecoStr = '';
                        if (data.logradouro) enderecoStr += data.logradouro;
                        if (data.numero) enderecoStr += `, ${data.numero}`;
                        if (data.bairro) enderecoStr += ` - ${data.bairro}`;
                        if (data.municipio && data.uf) enderecoStr += ` - ${data.municipio}/${data.uf}`;
                        if (enderecoStr) setEndereco(enderecoStr);

                        if (data.ddd_telefone_1) setTelefone(data.ddd_telefone_1);

                    } catch (error) {
                        console.error('Falha ao consultar CNPJ', error);
                    } finally {
                        setIsConsulting(false);
                    }
                }
            }
        } else {
            setCpfCnpjError('');
        }
    };

    const handleSave = async () => {
        if (!fornecedor) return;

        if (cnpj && !validateCpfCnpj(cnpj)) {
            setCpfCnpjError('Corrija o CPF ou CNPJ antes de salvar.');
            return;
        }

        const updatedFornecedor = { nome_razao_social: nome, cnpj_cpf: cnpj, telefone, email, endereco, tipo };
        try {
            await updateFornecedor(fornecedor.id, updatedFornecedor);
            onFornecedorUpdated();
            onClose();
        } catch (error) {
            console.error("Erro em updateFornecedor:", error);
            setSnackbarMessage('Erro ao atualizar fornecedor.');
            setIsSnackbarOpen(true);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Editar Fornecedor</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <TextField 
                    label="CNPJ/CPF" 
                    value={cnpj} 
                    onChange={handleCnpjCpfChange} 
                    required 
                    error={!!cpfCnpjError}
                    helperText={cpfCnpjError}
                    InputProps={{
                        endAdornment: isConsulting ? (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <TextField label="Nome/Razão Social" value={nome} onChange={e => setNome(e.target.value)} required />
                <TextField label="Telefone" value={telefone} onChange={e => setTelefone(e.target.value)} />
                <TextField label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                <TextField label="Endereço" value={endereco} onChange={e => setEndereco(e.target.value)} />
                <FormControl fullWidth>
                    <InputLabel>Tipo de Fornecimento</InputLabel>
                    <Select
                        value={tipo}
                        label="Tipo de Fornecimento"
                        onChange={(e) => setTipo(e.target.value as 'MATERIAL' | 'SERVICO' | 'AMBOS')}
                    >
                        <MenuItem value="SERVICO">Serviço</MenuItem>
                        <MenuItem value="MATERIAL">Material</MenuItem>
                        <MenuItem value="AMBOS">Ambos</MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" disabled={!!cpfCnpjError || isConsulting}>Salvar</Button>
            </DialogActions>

            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={6000}
                onClose={() => setIsSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setIsSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default FornecedorEditModal;
